import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchBar = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length > 1) {
      const debounce = setTimeout(() => {
        searchCryptos();
      }, 300);
      return () => clearTimeout(debounce);
    } else {
      setResults([]);
      setShowResults(false);
    }
  }, [query]);

  const searchCryptos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/crypto/search?q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setResults(response.data.data);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Error searching cryptos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (crypto) => {
    onSelect(crypto);
    setQuery('');
    setShowResults(false);
  };

  const formatPrice = (price) => {
    if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(6)}`;
  };

  return (
    <div ref={searchRef} className="relative w-64" data-testid="search-bar-container">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search crypto..."
          className="pl-10 pr-10"
          style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}
          data-testid="search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setShowResults(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            data-testid="search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-2 w-full rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto border"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
            data-testid="search-results"
          >
            {loading ? (
              <div className="p-4 text-center" style={{ color: '#475569' }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-6 h-6 border-2 rounded-full mx-auto"
                  style={{ borderColor: '#1D4ED8', borderTopColor: 'transparent' }}
                />
              </div>
            ) : results.length > 0 ? (
              <div className="py-2">
                {results.map((crypto) => {
                  const quote = crypto.quote?.USD;
                  if (!quote) return null;

                  return (
                    <motion.div
                      key={crypto.id}
                      whileHover={{ backgroundColor: '#F8FAFC' }}
                      onClick={() => handleSelect(crypto)}
                      className="px-4 py-3 cursor-pointer flex items-center justify-between"
                      data-testid={`search-result-${crypto.symbol}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#F8FAFC' }}>
                          <img
                            src={`https://s2.coinmarketcap.com/static/img/coins/64x64/${crypto.id}.png`}
                            alt={crypto.name}
                            className="w-6 h-6"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://cryptologos.cc/logos/${crypto.name.toLowerCase().replace(' ', '-')}-${crypto.symbol.toLowerCase()}-logo.png`;
                            }}
                          />
                        </div>
                        <div>
                          <div className="font-semibold" style={{ color: '#0F172A' }}>{crypto.name}</div>
                          <div className="text-xs" style={{ color: '#475569' }}>{crypto.symbol}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold" style={{ color: '#0F172A' }}>{formatPrice(quote.price)}</div>
                        <div className="text-xs" style={{ color: quote.percent_change_24h >= 0 ? '#16A34A' : '#DC2626' }}>
                          {quote.percent_change_24h >= 0 ? '+' : ''}{quote.percent_change_24h.toFixed(2)}%
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center" style={{ color: '#475569' }}>
                No results found
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
